import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject, Operation } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { isValidToken } from '@contexts/Shared/utils';
import { createUploadLink } from 'apollo-upload-client';
import { useMemo } from 'react';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

const authLink = setContext((_, { headers }) => {
  const token = (typeof window !== 'undefined' && localStorage.getItem('token')) || '';
  const validatedToken = isValidToken(token) ? token : 'sinToken';
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${validatedToken}` : '',
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, extensions, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${extensions?.code}, Path: ${path}`)
    );
  }
  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

const coreLink = errorLink.concat(
  authLink.concat(createUploadLink({ uri: process.env.NEXT_PUBLIC_REACT_APP_GRAPHQL_SERVER_URI }))
);

const creditStudyLink = errorLink.concat(
  authLink.concat(createUploadLink({ uri: process.env.NEXT_PUBLIC_REACT_APP_GRAPHQL_CREDIT_STUDY_SERVER_URI }))
);

const plutoLink = errorLink.concat(
  authLink.concat(createUploadLink({ uri: process.env.NEXT_PUBLIC_REACT_APP_GRAPHQL_PLUTO_SERVER_URI }))
);

export enum AptunoApolloLink {
  CORE = 'CORE',
  CREDIT_STUDY = 'CREDIT_STUDY',
  PLUTO = 'PLUTO',
}

const getApolloLink = (operation: Operation): ApolloLink => {
  let link: ApolloLink;
  switch (operation.getContext().clientName) {
    case AptunoApolloLink.CREDIT_STUDY:
      link = creditStudyLink;
      break;
    case AptunoApolloLink.PLUTO:
      link = plutoLink;
      break;
    default:
      link = coreLink;
      break;
  }
  return link;
};

const createApolloClient = () =>
  new ApolloClient({
    ssrMode: typeof window === 'undefined',
    cache: new InMemoryCache(),
    link: ApolloLink.split(
      (operation) => !!operation.getContext().clientName && operation.getContext().clientName !== AptunoApolloLink.CORE,
      ApolloLink.split(
        (operation) => operation.getContext().clientName === AptunoApolloLink.PLUTO,
        plutoLink,
        creditStudyLink
      ),
      coreLink
    ),
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });

export function initializeApollo(initialState = null): ApolloClient<unknown> {
  const _apolloClient = apolloClient ?? createApolloClient();

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState) {
    _apolloClient.cache.restore(initialState);
  }

  /**
   * SSG and SSR
   * Always create a new Apollo Client
   */
  if (typeof window === 'undefined') {
    return _apolloClient;
  }

  // Create the Apollo Client once in the client
  apolloClient = apolloClient ?? _apolloClient;

  return apolloClient;
}

export const getApolloClient = initializeApollo;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useApollo(initialState: any) {
  const apolloStore = useMemo(() => initializeApollo(initialState), [initialState]);
  return apolloStore;
}
